import * as i0 from '@angular/core';
import { InjectionToken, inject, InjectFlags, Injector, PLATFORM_ID, Injectable, NgZone } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { onSnapshot, initializeFirestore, queryEqual, collection, query, doc, writeBatch, runTransaction, Timestamp, DocumentSnapshot, getDoc, getDocs, Transaction, collectionGroup } from 'firebase/firestore';
import { onIdTokenChanged, getAuth, createUserWithEmailAndPassword, sendEmailVerification, signInAnonymously, signInWithEmailAndPassword, signInWithPopup, signInWithCustomToken, getAdditionalUserInfo, signOut } from 'firebase/auth';
import { Observable, ReplaySubject, timer, from, of, combineLatest, queueScheduler, asyncScheduler, shareReplay, startWith as startWith$1, firstValueFrom } from 'rxjs';
import { share, tap, startWith, map, switchMap, debounceTime, subscribeOn, observeOn, shareReplay as shareReplay$1, filter, delay } from 'rxjs/operators';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { initializeAnalytics } from 'firebase/analytics';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onValue, getDatabase, QueryConstraint, ref, query as query$1, set, update, remove, serverTimestamp, get, push } from 'firebase/database';
import { getStorage, ref as ref$1, uploadBytesResumable } from 'firebase/storage';

const FIREBASE_CONFIG = new InjectionToken('Firebase Config');
const REGION_OR_DOMAIN = new InjectionToken('Firebase cloud functions region or domain');
const FIRESTORE_SETTINGS = new InjectionToken('Firestore settings');
const ANALYTICS_SETTINGS = new InjectionToken('Analytics settings');
function getConfig() {
    try {
        return inject(FIREBASE_CONFIG);
    }
    catch (err) {
        const message = `You should add FIREBASE_CONFIG token to you root module providers (probably AppModule).
Example:
  
@NgModule({
  declarations: [...],
  imports: [...],
  providers: [{ provide: FIREBASE_CONFIG, useValue: environment.firebase }] <--- Add this
  ...
})

Original message: ${err.message}`;
        throw new Error(message);
    }
}

const FIREBASE_APP = new InjectionToken('Firebase application', {
    providedIn: 'root',
    factory: () => {
        const config = inject(FIREBASE_CONFIG);
        return initializeApp(config.options);
    },
});

const DEFAULT_OPTIONS = { includeMetadataChanges: false };
function fromRef(ref, options = DEFAULT_OPTIONS) {
    /* eslint-enable @typescript-eslint/no-explicit-any */
    return new Observable(subscriber => {
        const unsubscribe = onSnapshot(ref, options, {
            next: subscriber.next.bind(subscriber),
            error: subscriber.error.bind(subscriber),
            complete: subscriber.complete.bind(subscriber),
        });
        return { unsubscribe };
    });
}
function user(auth) {
    return new Observable(subscriber => {
        const unsubscribe = onIdTokenChanged(auth, subscriber.next.bind(subscriber), subscriber.error.bind(subscriber), subscriber.complete.bind(subscriber));
        return { unsubscribe };
    });
}
function shareWithDelay(delay = 100) {
    return share({
        connector: () => new ReplaySubject(1),
        resetOnRefCountZero: () => timer(delay),
        resetOnError: true,
        resetOnComplete: false,
    });
}
/**
 * Operator that join the source with sub queries.
 * There are two stategies :
 * 1. `shouldAwait: true`: Await all subqueries to emit once before emitting a next value
 * 2. `shouldAwait: false`: Emit the source and hydrate it with the subqueries along the way
 * @example
 * ```typescript
 * of({ docUrl: '...' }).valueChanges().pipe(
 *   joinWith({
 *     doc: source => fetch(docUrl).then(res => res.json()),
 *   }, { shouldAwait: true })
 * ).subscribe(res => console.log(res.subQuery))
 * ```
 * @param queries A map of subqueries to apply. Each query can return a static value, Promise or Observable
 * @param options Strategy to apply on the joinWith
 */
function joinWith(queries, options = {}) {
    const shouldAwait = options.shouldAwait ?? true;
    const debounce = options.debounceTime ?? 100;
    const runQuery = (entity) => {
        const obs = [];
        for (const key in queries) {
            // Transform return value into an observable
            let result = queries[key](entity);
            if (!(result instanceof Observable)) {
                if (result instanceof Promise) {
                    result = from(result);
                }
                else {
                    result = of(result);
                }
            }
            // Hydrate the entity with the data
            let observe;
            if (shouldAwait) {
                observe = result.pipe(tap(result => entity[key] = result));
            }
            else {
                observe = result.pipe(startWith(undefined), tap(result => entity[key] = result));
            }
            obs.push(observe);
        }
        if (!obs.length)
            return of(entity);
        return combineLatest(obs).pipe(map(() => {
            if (!entity)
                return entity;
            return JSON.parse(JSON.stringify(entity), jsonDateReviver);
        }));
    };
    return switchMap((data) => {
        if (Array.isArray(data)) {
            if (!data.length)
                return of([]);
            return combineLatest(data.map(runQuery)).pipe(debounceTime(debounce));
        }
        return runQuery(data);
    });
}
function jsonDateReviver(_, value) {
    if (!value)
        return value;
    const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,}|)Z$/;
    if (typeof value === 'string' && dateFormat.test(value))
        return new Date(value);
    if (typeof value === 'object' &&
        Object.keys(value).length === 2 &&
        ['nanoseconds', 'seconds'].every((k) => k in value))
        return new Date(((value.nanoseconds * 1) ^ -6) + value.seconds * 1000);
    return value;
}

function exist$1(doc) {
    return doc !== undefined && doc !== null;
}
function isNotUndefined(doc) {
    return doc !== undefined;
}
///////////////
// TIMESTAMP //
///////////////
//////////
// PATH //
//////////
function isDocPath(path) {
    return path.split('/').length % 2 === 0;
}
// Check if a string is a full path
function isPathRef(path) {
    return !!((typeof path === "string") && (path.split('/').length > 1) && !path.includes(':'));
}
function isIdList(idsOrQuery) {
    return idsOrQuery.every(id => typeof id === 'string');
}
/** Get the params from a path */
function getPathParams$1(path) {
    return path
        .split('/')
        .filter((segment) => segment.charAt(0) === ':')
        .map((segment) => segment.substring(1));
}
function assertPath(path) {
    for (const segment of path.split('/')) {
        if (segment.charAt(0) === ':') {
            const key = segment.substring(1);
            throw new Error(`Required parameter ${key} from ${path} has not been provided`);
        }
    }
}
function assertCollection(path) {
    if (isDocPath(path)) {
        throw new Error(`Expected collection path but got: ${path}`);
    }
}
/**
 * Transform a path based on the params
 * @param path The path with params starting with "/:"
 * @param params A map of id params
 * @example pathWithParams('movies/:movieId/stakeholder/:shId', { movieId, shId })
 */
function pathWithParams(path, params) {
    if (!params)
        return path;
    if (!path.includes(':'))
        return path;
    return path
        .split('/')
        .map((segment) => {
        if (segment.charAt(0) === ':') {
            const key = segment.substr(1);
            return params[key] || segment;
        }
        else {
            return segment;
        }
    })
        .join('/');
}
////////////////
// REFERENCES //
////////////////
function isQuery(ref) {
    return ref.type === 'query';
}
function isCollectionRef(ref) {
    return ref.type === 'collection';
}

class ɵZoneScheduler {
    constructor(zone, delegate = queueScheduler) {
        this.zone = zone;
        this.delegate = delegate;
    }
    now() {
        return this.delegate.now();
    }
    schedule(work, delay, state) {
        const targetZone = this.zone;
        // Wrap the specified work function to make sure that if nested scheduling takes place the
        // work is executed in the correct zone
        const workInZone = function (state) {
            targetZone.runGuarded(() => {
                work.apply(this, [state]);
            });
        };
        // Scheduling itself needs to be run in zone to ensure setInterval calls for async scheduling are done
        // inside the correct zone. This scheduler needs to schedule asynchronously always to ensure that
        // firebase emissions are never synchronous. Specifying a delay causes issues with the queueScheduler delegate.
        return this.delegate.schedule(workInZone, delay, state);
    }
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() { }
class BlockUntilFirstOperator {
    constructor(zone) {
        this.zone = zone;
        this.task = null;
    }
    call(subscriber, source) {
        const unscheduleTask = this.unscheduleTask.bind(this);
        this.task = this.zone.run(() => Zone.current.scheduleMacroTask('firebaseZoneBlock', noop, {}, noop, noop));
        return source.pipe(tap({ next: unscheduleTask, complete: unscheduleTask, error: unscheduleTask })).subscribe(subscriber).add(unscheduleTask);
    }
    unscheduleTask() {
        // maybe this is a race condition, invoke in a timeout
        // hold for 10ms while I try to figure out what is going on
        setTimeout(() => {
            if (this.task != null && this.task.state === 'scheduled') {
                this.task.invoke();
                this.task = null;
            }
        }, 10);
    }
}
function keepUnstableUntilFirst(ngZone) {
    return (obs$) => {
        obs$ = obs$.lift(new BlockUntilFirstOperator(ngZone));
        return obs$.pipe(
        // Run the subscribe body outside of Angular (e.g. calling Firebase SDK to add a listener to a change event)
        subscribeOn(ngZone.runOutsideAngular(() => new ɵZoneScheduler(Zone.current))), 
        // Run operators inside the angular zone (e.g. side effects via tap())
        observeOn(ngZone.run(() => new ɵZoneScheduler(Zone.current, asyncScheduler))));
    };
}

const FIRESTORE = new InjectionToken('Firestore instance', {
    providedIn: 'root',
    factory: () => {
        const config = getConfig();
        const settings = inject(FIRESTORE_SETTINGS, InjectFlags.Optional);
        const app = inject(FIREBASE_APP);
        const firestore = initializeFirestore(app, settings ?? {});
        if (config.firestore)
            config.firestore(firestore);
        return firestore;
    },
});

class FirestoreService {
    constructor() {
        this.memoryQuery = new Map();
        this.memoryRef = {};
        this.injector = inject(Injector);
        this.plateformId = inject(PLATFORM_ID);
        /** Transfer state between server and  */
        this.transferState = inject(TransferState, InjectFlags.Optional);
        /** Cache based state for document */
        this.state = new Map();
    }
    get db() {
        return this.injector.get(FIRESTORE);
    }
    /** @internal Should only be used by FireCollection services */
    setState(ref, snap) {
        if (isCollectionRef(ref)) {
            snap.forEach(doc => this.state.set(doc.ref.path, doc));
            this.state.set(ref.path, snap);
        }
        else if (isQuery(ref)) {
            snap.forEach(doc => this.state.set(doc.ref.path, doc));
            for (const key of this.state.keys()) {
                if (typeof key !== 'string' && queryEqual(key, ref))
                    return;
            }
        }
        else {
            this.state.set(ref.path, snap);
        }
    }
    getState(ref) {
        if (isQuery(ref)) {
            for (const key of this.state.keys()) {
                if (typeof key !== 'string' && queryEqual(key, ref))
                    return this.state.get(key);
            }
            return;
        }
        else {
            return this.state.get(ref.path);
        }
    }
    /** @internal Should only be used by FireCollection services */
    fromMemory(ref) {
        if (!isQuery(ref)) {
            const path = ref.path;
            if (!this.memoryRef[path]) {
                this.memoryRef[path] = fromRef(ref).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
            }
            return this.memoryRef[path];
        }
        else {
            let existing = null;
            for (const [key, value] of this.memoryQuery.entries()) {
                if (typeof key !== 'string' && queryEqual(key, ref)) {
                    existing = value;
                    break;
                }
            }
            if (existing)
                return existing;
            // We remove delay because firestore cache can create side effect when active onSnapshot overlap
            // TODO: check how to leverage native cache from JS sdk
            const observable = fromRef(ref).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
            this.memoryQuery.set(ref, observable);
            return observable;
        }
    }
    getTransfer(ref) {
        if (!this.transferState || !isPlatformBrowser(this.plateformId))
            return;
        if (isQuery(ref))
            return;
        const key = makeStateKey(ref.path);
        if (!this.transferState.hasKey(key))
            return;
        const value = this.transferState.get(key, undefined);
        this.transferState.remove(key);
        return value;
    }
    setTransfer(ref, value) {
        if (!value)
            return;
        if (!this.transferState || !isPlatformServer(this.plateformId))
            return;
        if (Array.isArray(ref) && Array.isArray(value)) {
            ref.forEach((reference, i) => this.setTransfer(reference, value[i]));
        }
        else if (!Array.isArray(ref)) {
            if (isQuery(ref))
                return;
            this.transferState.set(makeStateKey(ref.path), value);
        }
    }
    clearCache(paths) {
        if (!paths)
            return;
        if (Array.isArray(paths)) {
            for (const path of paths) {
                delete this.memoryRef[path];
                this.state.delete(path);
            }
        }
        else if (typeof paths === 'string') {
            delete this.memoryRef[paths];
            this.state.delete(paths);
        }
        else {
            for (const key of this.memoryQuery.keys()) {
                if (queryEqual(key, paths)) {
                    this.memoryQuery.delete(paths);
                    break;
                }
            }
            for (const key of this.state.keys()) {
                if (typeof key !== 'string' && queryEqual(key, paths)) {
                    this.memoryQuery.delete(paths);
                    break;
                }
            }
        }
    }
    // overload used internally when looping over paths array
    getRef(paths, constraints) {
        if (!arguments.length || !paths)
            return undefined;
        // Array of docs
        if (Array.isArray(paths)) {
            return paths.map((path) => this.getRef(path));
        }
        const hasContraints = Array.isArray(constraints);
        if (hasContraints) {
            assertPath(paths);
            assertCollection(paths);
            const ref = collection(this.db, paths);
            return query(ref, ...constraints);
        }
        else {
            assertPath(paths);
            if (isDocPath(paths))
                return doc(this.db, paths);
            return collection(this.db, paths);
        }
    }
    batch() {
        return writeBatch(this.db);
    }
    runTransaction(cb) {
        return runTransaction(this.db, (tx) => cb(tx));
    }
    createId() {
        return doc(collection(this.db, '__')).id;
    }
}
FirestoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FirestoreService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FirestoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FirestoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FirestoreService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/* eslint-disable @typescript-eslint/no-non-null-assertion */
/////////////
// HELPERS //
/////////////
/** Return the full path of the doc */
function getDocPath(path, id) {
    // If path is smaller than id, id is the full path from ref
    return path.split('/').length < id.split('/').length ? id : `${path}/${id}`;
}
/** Recursively all Timestamp into Date */
function toDate$1(target) {
    if (typeof target !== 'object')
        return target;
    for (const key in target) {
        const value = target[key];
        if (!value || typeof value !== 'object')
            continue;
        if (value instanceof Timestamp) {
            target[key] = value.toDate();
            continue;
        }
        toDate$1(value);
    }
    return target;
}
/////////////
// SERVICE //
/////////////
class FireCollection {
    constructor() {
        this.platformId = inject(PLATFORM_ID);
        this.zone = inject(NgZone);
        this.firestore = inject(FirestoreService);
        this.idKey = 'id';
        // If true, will store the document id (IdKey) onto the document
        this.storeId = false;
        this.memorize = false;
    }
    get db() {
        return this.firestore.db;
    }
    useCache(ref) {
        if (!this.memorize)
            return fromRef(ref).pipe(map(snap => this.snapToData(snap)));
        const transfer = this.firestore.getTransfer(ref);
        const initial = this.firestore.getState(ref);
        const snap$ = this.firestore.fromMemory(ref).pipe(tap(snap => this.firestore.setState(ref, snap)));
        if (transfer)
            return snap$.pipe(map(snap => this.snapToData(snap)), startWith$1(transfer));
        if (initial)
            return snap$.pipe(startWith$1(initial), map(snap => this.snapToData(snap)));
        return snap$.pipe(map(snap => this.snapToData(snap)));
    }
    clearCache(refs) {
        if (Array.isArray(refs))
            return this.firestore.clearCache(refs.map(ref => ref.path));
        if (isQuery(refs))
            return this.firestore.clearCache(refs);
        return this.firestore.clearCache(refs?.path);
    }
    /** Function triggered when adding/updating data to firestore */
    toFirestore(entity, actionType) {
        if (actionType === 'add') {
            const _meta = { createdAt: new Date(), modifiedAt: new Date() };
            return { _meta, ...entity };
        }
        else {
            return { ...entity, '_meta.modifiedAt': new Date() };
        }
    }
    /** Function triggered when getting data from firestore */
    fromFirestore(snapshot) {
        if (snapshot.exists()) {
            return { ...toDate$1(snapshot.data()), [this.idKey]: snapshot.id };
        }
        else {
            return undefined;
        }
    }
    batch() {
        return writeBatch(this.db);
    }
    runTransaction(cb) {
        return runTransaction(this.db, (tx) => cb(tx));
    }
    createId() {
        return doc(collection(this.db, '__')).id;
    }
    snapToData(snap) {
        if (snap instanceof DocumentSnapshot)
            return this.fromFirestore(snap);
        const snaps = Array.isArray(snap) ? snap : snap.docs;
        return snaps.map(s => this.snapToData(s)).filter(isNotUndefined);
    }
    async getFromRef(ref) {
        if (Array.isArray(ref))
            return Promise.all(ref.map(getDoc)).then(snaps => this.snapToData(snaps));
        const snap = (ref.type === 'document') ? await getDoc(ref) : await getDocs(ref);
        return this.snapToData(snap);
    }
    fromRef(ref) {
        if (isPlatformServer(this.platformId)) {
            return this.zone.runOutsideAngular(() => from(this.getFromRef(ref))).pipe(tap(value => this.firestore.setTransfer(ref, value)), keepUnstableUntilFirst(this.zone));
        }
        if (Array.isArray(ref)) {
            if (!ref.length)
                return of([]);
            const queries = ref.map(r => this.useCache(r));
            return combineLatest(queries);
        }
        else {
            return this.useCache(ref);
        }
    }
    getRef(ids, parameters) {
        // Collection
        if (!arguments.length)
            return this.firestore.getRef(this.path);
        // Id is undefined or null
        if (!ids)
            return undefined;
        if (Array.isArray(ids)) {
            // List of ref
            if (ids.every(isPathRef))
                return this.firestore.getRef(ids);
            const path = pathWithParams(this.path, parameters);
            // List of ids
            if (isIdList(ids))
                return this.firestore.getRef(ids.map((id) => getDocPath(path, id)));
            // List of constraints
            return this.firestore.getRef(path, ids);
        }
        if (typeof ids === 'string') {
            // Ref
            if (isPathRef(ids))
                return this.firestore.getRef(ids);
            // Id
            const path = pathWithParams(this.path, parameters);
            return this.firestore.getRef(getDocPath(path, ids));
        }
        // Subcollection
        return this.firestore.getRef(pathWithParams(this.path, ids));
    }
    async reload() {
        if (!this.memorize)
            return;
        const ref = this.getRef(...arguments);
        if (!ref)
            return;
        this.clearCache(ref);
        return this.load(...arguments);
    }
    async load() {
        return firstValueFrom(this.valueChanges(...arguments));
    }
    async getValue() {
        const ref = this.getRef(...arguments);
        if (!ref)
            return;
        return this.getFromRef(ref);
    }
    valueChanges(idOrQuery) {
        if (Array.isArray(idOrQuery) && !idOrQuery.length)
            return of([]);
        const ref = this.getRef(...arguments);
        if (!ref)
            return of(undefined);
        return this.fromRef(ref);
    }
    async upsert(documents, options = {}) {
        const doesExist = async (doc) => {
            const id = doc[this.idKey];
            if (typeof id !== 'string')
                return false;
            const ref = this.getRef(id, options.params);
            const snap = (options.write instanceof Transaction)
                ? await options.write?.get(ref)
                : await getDoc(ref);
            return snap.exists();
        };
        const upsert = async (doc) => {
            const exists = await doesExist(doc);
            if (!exists)
                return this.add(doc, options);
            await this.update(doc, options);
            return doc[this.idKey];
        };
        return Array.isArray(documents)
            ? Promise.all(documents.map(upsert))
            : upsert(documents);
    }
    async add(documents, options = {}) {
        const docs = Array.isArray(documents) ? documents : [documents];
        const { write = this.batch(), ctx } = options;
        const operations = docs.map(async (value) => {
            const id = value[this.idKey] || this.createId();
            const data = await this.toFirestore(value, 'add');
            if (this.storeId)
                data[this.idKey] = id;
            const ref = this.getRef(id, options.params);
            write.set(ref, data);
            if (this.onCreate) {
                await this.onCreate(data, { write, ctx });
            }
            return id;
        });
        const ids = await Promise.all(operations);
        // If there is no atomic write provided
        if (!options.write) {
            await write.commit();
        }
        return Array.isArray(documents) ? ids : ids[0];
    }
    /**
     * Remove one or several document from Firestore
     * @param id A unique or list of id representing the document
     * @param options options to write the document on firestore
     */
    async remove(id, options = {}) {
        const { write = this.batch(), ctx } = options;
        const ids = Array.isArray(id) ? id : [id];
        const refs = [];
        const operations = ids.map(async (docId) => {
            const ref = this.getRef(docId, options.params);
            write.delete(ref);
            if (this.onDelete) {
                await this.onDelete(docId, { write, ctx });
            }
            refs.push(ref);
        });
        await Promise.all(operations);
        // If there is no atomic write provided
        if (!options.write) {
            await write.commit();
            if (this.memorize)
                this.clearCache(refs);
        }
    }
    /** Remove all document of the collection */
    async removeAll(options = {}) {
        const ref = options.params ? this.getRef(options.params) : this.getRef();
        const snapshot = await getDocs(ref);
        const ids = snapshot.docs.map((doc) => doc.id);
        await this.remove(ids, options);
        if (this.memorize)
            this.clearCache(ref);
    }
    async update(idsOrEntity, stateFnOrWrite, options = {}) {
        let ids = [];
        let stateFunction;
        let getData;
        const isEntity = (value) => {
            return typeof value === 'object' && value[this.idKey];
        };
        const isEntityArray = (values) => {
            return Array.isArray(values) && values.every((value) => isEntity(value));
        };
        if (isEntity(idsOrEntity)) {
            ids = [idsOrEntity[this.idKey]];
            getData = () => idsOrEntity;
            options = stateFnOrWrite || {};
        }
        else if (isEntityArray(idsOrEntity)) {
            const entityMap = new Map(idsOrEntity.map((entity) => [entity[this.idKey], entity]));
            ids = Array.from(entityMap.keys());
            getData = (docId) => entityMap.get(docId);
            options = stateFnOrWrite || {};
        }
        else if (typeof stateFnOrWrite === 'function') {
            ids = Array.isArray(idsOrEntity) ? idsOrEntity : [idsOrEntity];
            stateFunction = stateFnOrWrite;
        }
        else if (typeof stateFnOrWrite === 'object') {
            ids = Array.isArray(idsOrEntity) ? idsOrEntity : [idsOrEntity];
            getData = () => stateFnOrWrite;
        }
        else {
            throw new Error('Passed parameters match none of the function signatures.');
        }
        const { ctx } = options;
        if (!Array.isArray(ids) || !ids.length) {
            return;
        }
        // If update depends on the entity, use transaction
        if (stateFunction) {
            let refs = [];
            await runTransaction(this.db, async (tx) => {
                refs = [];
                const operations = ids.map(async (id) => {
                    const ref = this.getRef(id, options.params);
                    refs.push(ref);
                    const snapshot = await tx.get(ref);
                    const doc = this.fromFirestore(snapshot);
                    if (doc && stateFunction) {
                        const data = await stateFunction(doc, tx);
                        const result = await this.toFirestore(data, 'update');
                        tx.update(ref, result);
                        if (this.onUpdate) {
                            await this.onUpdate(data, { write: tx, ctx });
                        }
                    }
                    return tx;
                });
                return Promise.all(operations);
            });
            if (this.memorize)
                this.clearCache(refs);
        }
        else {
            const { write = this.batch() } = options;
            const refs = [];
            const operations = ids.map(async (docId) => {
                const doc = getData(docId);
                if (!docId) {
                    throw new Error(`Document should have an unique id to be updated, but none was found in ${doc}`);
                }
                const ref = this.getRef(docId, options.params);
                refs.push(ref);
                const data = await this.toFirestore(doc, 'update');
                write.update(ref, data);
                if (this.onUpdate) {
                    await this.onUpdate(doc, { write, ctx });
                }
            });
            await Promise.all(operations);
            // If there is no atomic write provided
            if (!options.write) {
                await write.commit();
                if (this.memorize)
                    this.clearCache(refs);
            }
        }
    }
}

/** Get the params from a path */
function getPathParams(path) {
    return path
        .split('/')
        .filter((segment) => segment.charAt(0) === ':')
        .map((segment) => segment.substring(1));
}
class FireSubCollection extends FireCollection {
    constructor() {
        super(...arguments);
        this.pathKey = 'path';
    }
    get groupId() {
        return this.path.split('/').pop();
    }
    /** Function triggered when getting data from firestore */
    fromFirestore(snapshot) {
        if (snapshot.exists()) {
            return {
                ...toDate$1(snapshot.data()),
                [this.idKey]: snapshot.id,
                [this.pathKey]: snapshot.ref.path
            };
        }
        else {
            return undefined;
        }
    }
    getGroupRef(constraints) {
        const group = collectionGroup(this.db, this.groupId);
        if (!arguments.length)
            return group;
        if (!constraints)
            return;
        return query(group, ...constraints);
    }
    /** Observable the content of group reference(s)  */
    fromGroupRef(ref) {
        if (isPlatformServer(this.platformId)) {
            return this.zone.runOutsideAngular(() => from(this.getFromRef(ref))).pipe(keepUnstableUntilFirst(this.zone));
        }
        return this.useCache(ref);
    }
    async getValue(idOrQuery, params) {
        // If array is empty
        if (Array.isArray(idOrQuery) && !idOrQuery.length)
            return [];
        // Group query
        const isEmpty = arguments.length === 0;
        const isGroupQuery = arguments.length === 1 && Array.isArray(idOrQuery) && !isIdList(idOrQuery);
        // Collection Query
        const ref = (isEmpty || isGroupQuery)
            ? this.getGroupRef(...arguments)
            : this.getRef(...arguments);
        if (!ref)
            return;
        return this.getFromRef(ref);
    }
    async reload(idOrQuery, params) {
        const isEmpty = arguments.length === 0;
        const isGroupQuery = arguments.length === 1 && Array.isArray(idOrQuery) && !isIdList(idOrQuery);
        const ref = (isEmpty || isGroupQuery)
            ? this.getGroupRef(...arguments)
            : this.getRef(...arguments);
        if (!ref)
            return;
        if (this.memorize) {
            Array.isArray(ref)
                ? ref.forEach(r => this.clearCache(r))
                : this.clearCache(ref);
        }
        return this.load(...arguments);
    }
    load() {
        return firstValueFrom(this.valueChanges(...arguments));
    }
    valueChanges(idOrQuery, params) {
        // If array is empty
        if (Array.isArray(idOrQuery) && !idOrQuery.length)
            return of([]);
        // Check if group query
        const isEmpty = arguments.length === 0;
        const isGroupQuery = arguments.length === 1 && Array.isArray(idOrQuery) && !isIdList(idOrQuery);
        // Group or Collection Query
        const ref = (isEmpty || isGroupQuery)
            ? this.getGroupRef(...arguments)
            : this.getRef(...arguments);
        if (!ref)
            return of(undefined);
        return this.fromRef(ref);
    }
}

const exist = (v) => v !== null && v !== undefined;
const FIRE_AUTH = new InjectionToken('Fire auth instance', {
    providedIn: 'root',
    factory: () => {
        const config = getConfig();
        const app = inject(FIREBASE_APP);
        const auth = getAuth(app);
        if (config.auth)
            config.auth(auth);
        return auth;
    },
});
/**
 * Get the custom claims of a user. If no key is provided, return the whole claims object
 * @param user The user object returned by Firebase Auth
 * @param roles Keys of the custom claims inside the claim objet
 */
async function getCustomClaims(user, keys) {
    if (!user)
        return {};
    const { claims } = await user.getIdTokenResult();
    if (!keys)
        return claims;
    const fields = Array.isArray(keys) ? keys : [keys];
    const result = {};
    for (const key of fields) {
        if (claims[key]) {
            result[key] = claims[key];
        }
    }
    return result;
}
function isUpdateCallback(update) {
    return typeof update === 'function';
}
class BaseFireAuth {
    constructor() {
        this.memoProfile = {};
        this.platformId = inject(PLATFORM_ID);
        this.getAuth = inject(FIRE_AUTH);
        this.injector = inject(Injector);
        this.zone = inject(NgZone);
        this.idKey = 'id';
        this.user$ = isPlatformServer(this.platformId)
            ? this.zone.runOutsideAngular(() => user(this.auth))
            : user(this.auth).pipe(shareReplay$1({ refCount: true, bufferSize: 1 }));
        /**
         * Observe current user. Doesn't emit if there are no user connected.
         * Use `user` if you need to know if user is connected
         */
        this.currentUser$ = this.user$.pipe(filter(exist));
        /** Listen on changes from the authenticated user */
        this.profile$ = this.user$.pipe(map((user) => this.getRef({ user })), switchMap((ref) => (ref ? this.useMemo(ref) : of(undefined))), map(snapshot => snapshot ? this.fromFirestore(snapshot) : undefined));
    }
    get db() {
        return this.injector.get(FIRESTORE);
    }
    get auth() {
        return this.injector.get(FIRE_AUTH);
    }
    get user() {
        return this.auth.currentUser;
    }
    useMemo(ref) {
        if (isPlatformServer(this.platformId)) {
            return this.zone.runOutsideAngular(() => from(getDoc(ref))).pipe(keepUnstableUntilFirst(this.zone));
        }
        if (!this.memoProfile[ref.path]) {
            this.memoProfile[ref.path] = fromRef(ref).pipe(shareWithDelay(100));
        }
        return this.memoProfile[ref.path];
    }
    /**
     * Select the roles for this user. Can be in custom claims or in a Firestore collection
     * @param user The user given by FireAuth
     * @see getCustomClaims to get the custom claims out of the user
     * @note Can be overwritten
     */
    selectRoles(user) {
        return getCustomClaims(user);
    }
    /**
     * Function triggered when getting data from firestore
     * @note should be overwritten
     */
    fromFirestore(snapshot) {
        return snapshot.exists()
            ? { ...toDate$1(snapshot.data()), [this.idKey]: snapshot.id }
            : undefined;
    }
    /**
     * Function triggered when adding/updating data to firestore
     * @note should be overwritten
     */
    toFirestore(profile, actionType) {
        if (actionType === 'add') {
            const _meta = { createdAt: new Date(), modifiedAt: new Date() };
            return { _meta, ...profile };
        }
        else {
            return { ...profile, '_meta.modifiedAt': new Date() };
        }
    }
    /**
     * Function triggered when transforming a user into a profile
     * @param user The user object from FireAuth
     * @param ctx The context given on signup
     */
    createProfile(user, ctx) {
        return { avatar: user?.photoURL, displayName: user?.displayName };
    }
    /** Triggerd when creating or getting a user */
    useCollection(user) {
        return this.path ?? null;
    }
    /** If user connected, return its document in Firestore,  */
    getRef(options = {}) {
        const user = options.user ?? this.user;
        if (user) {
            return doc(this.db, `${this.path}/${user.uid}`);
        }
        return;
    }
    /** Return current user. Only return when auth has emit */
    awaitUser() {
        return firstValueFrom(this.user$);
    }
    /** Get the current user Profile from Firestore */
    async getValue() {
        const ref = this.getRef();
        if (ref) {
            const snapshot = await getDoc(ref);
            return this.fromFirestore(snapshot);
        }
        return;
    }
    /**
     * @description Delete user from authentication service and database
     * WARNING This is security sensitive operation
     */
    async delete(options = {}) {
        const user = this.user;
        const ref = this.getRef({ user });
        if (!user || !ref) {
            throw new Error('No user connected');
        }
        const { write = writeBatch(this.db), ctx } = options;
        write.delete(ref);
        if (this.onDelete)
            await this.onDelete({ write, ctx });
        if (!options.write) {
            await write.commit();
        }
        return user.delete();
    }
    /** Update the current profile of the authenticated user */
    async update(profile, options = {}) {
        const ref = this.getRef();
        if (!ref) {
            throw new Error('No user connected.');
        }
        if (isUpdateCallback(profile)) {
            return runTransaction(this.db, async (tx) => {
                const snapshot = (await tx.get(ref));
                const doc = this.fromFirestore(snapshot);
                if (!doc) {
                    throw new Error(`Could not find document at "${this.path}/${snapshot.id}"`);
                }
                const data = await profile(this.toFirestore(doc, 'update'), tx);
                tx.update(ref, data);
                if (this.onUpdate)
                    await this.onUpdate(data, { write: tx, ctx: options.ctx });
                return tx;
            });
        }
        else if (typeof profile === 'object') {
            const { write = writeBatch(this.db), ctx } = options;
            write.update(ref, this.toFirestore(profile, 'update'));
            if (this.onUpdate)
                await this.onUpdate(profile, { write, ctx });
            // If there is no atomic write provided
            if (!options.write) {
                return write.commit();
            }
        }
    }
    /** Manage the creation of the user into Firestore */
    async create(cred, options) {
        const user = cred.user;
        if (!user) {
            throw new Error('Could not create an account');
        }
        const { write = writeBatch(this.db), ctx, collection } = options;
        if (this.onSignup)
            await this.onSignup(cred, { write, ctx, collection });
        const ref = this.getRef({ user, collection });
        if (ref) {
            const profile = await this.createProfile(user, ctx);
            write.set(ref, this.toFirestore(profile, 'add'));
            if (this.onCreate)
                await this.onCreate(profile, { write, ctx, collection });
            if (!options.write) {
                await write.commit();
            }
        }
        return cred;
    }
}
BaseFireAuth.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: BaseFireAuth, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BaseFireAuth.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: BaseFireAuth, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: BaseFireAuth, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
class FireAuth extends BaseFireAuth {
    /**
     * Create a user based on email and password
     * Will send a verification email to the user if verificationURL is provided config
     */
    async signup(email, password, options = {}) {
        const cred = await createUserWithEmailAndPassword(this.auth, email, password);
        if (this.verificationUrl) {
            const url = this.verificationUrl;
            await sendEmailVerification(cred.user, { url });
        }
        return this.create(cred, options);
    }
    async signin(provider, passwordOrOptions, options) {
        try {
            let cred;
            if (!provider) {
                cred = await signInAnonymously(this.auth);
            }
            else if (passwordOrOptions &&
                typeof provider === 'string' &&
                typeof passwordOrOptions === 'string') {
                cred = await signInWithEmailAndPassword(this.auth, provider, passwordOrOptions);
            }
            else if (typeof provider === 'object') {
                cred = await signInWithPopup(this.auth, provider);
            }
            else {
                cred = await signInWithCustomToken(this.auth, provider);
            }
            if (!cred.user) {
                throw new Error('Could not find credential for signin');
            }
            // Signup: doesn't trigger onSignin
            if (getAdditionalUserInfo(cred)?.isNewUser) {
                options = typeof passwordOrOptions === 'object' ? passwordOrOptions : {};
                return this.create(cred, options);
            }
            if (this.onSignin)
                await this.onSignin(cred);
            return cred;
        }
        catch (err) {
            if (err.code === 'auth/operation-not-allowed') {
                console.warn('You tried to connect with a disabled auth provider. Enable it in Firebase console');
            }
            throw err;
        }
    }
    /** Signs out the current user and clear the store */
    async signout() {
        await signOut(this.auth);
        if (this.onSignout)
            await this.onSignout();
    }
}
FireAuth.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireAuth, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
FireAuth.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireAuth, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireAuth, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const FIRE_ANALYTICS = new InjectionToken('Firebase Analytics instance', {
    providedIn: 'root',
    factory: () => {
        const config = getConfig();
        const settings = inject(ANALYTICS_SETTINGS, InjectFlags.Optional);
        const app = inject(FIREBASE_APP);
        const analytics = initializeAnalytics(app, settings ?? {});
        if (config.analytics)
            config.analytics(analytics);
        return analytics;
    },
});

const CLOUD_FUNCTIONS = new InjectionToken('Firebase cloud functions', {
    providedIn: 'root',
    factory: () => {
        const config = getConfig();
        const regionOrDomain = inject(REGION_OR_DOMAIN, InjectFlags.Optional);
        const app = inject(FIREBASE_APP);
        const functions = getFunctions(app, regionOrDomain ?? undefined);
        if (config.functions)
            config.functions(functions);
        return functions;
    },
});
class CallableFunctions {
    constructor() {
        this.injector = inject(Injector);
        this.callables = {};
    }
    get function() {
        return this.injector.get(CLOUD_FUNCTIONS);
    }
    prepare(name) {
        if (!this.callables[name]) {
            this.callables[name] = httpsCallable(this.function, name);
        }
        return (data) => this.call(name, data);
    }
    async call(name, data, options) {
        if (!this.callables[name]) {
            this.callables[name] = httpsCallable(this.function, name, options);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const result = await this.callables[name](data);
        return result.data;
    }
}
CallableFunctions.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: CallableFunctions, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CallableFunctions.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: CallableFunctions, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: CallableFunctions, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Create an observable from a Database Reference or Database Query.
 * @param query Database Reference
 */
function fromQuery(query) {
    return new Observable((subscriber) => {
        const unsubscribe = onValue(query, (snapshot) => subscriber.next(snapshot), subscriber.error.bind(subscriber));
        return { unsubscribe };
    }).pipe(
    // Ensures subscribe on observable is async. This handles
    // a quirk in the SDK where on/once callbacks can happen
    // synchronously.
    delay(0));
}

const DB_URL = new InjectionToken('The URL of the Realtime Database instance to connect to');
const DATABASE = new InjectionToken('Database instance', {
    providedIn: 'root',
    factory: () => {
        const url = inject(DB_URL, InjectFlags.Optional);
        const config = getConfig();
        const app = inject(FIREBASE_APP);
        const db = getDatabase(app, url ?? undefined);
        if (config.database)
            config.database(db);
        return db;
    },
});

function isContraintList(idsOrQuery) {
    return idsOrQuery.every(query => query instanceof QueryConstraint);
}
class FireDatabase {
    constructor() {
        this.injector = inject(Injector);
        this.memory = new Map();
    }
    get db() {
        return this.injector.get(DATABASE);
    }
    getRef(paths, paramsOrConstraints, params) {
        if (!arguments.length || !paths)
            return undefined;
        const hasContraints = Array.isArray(paramsOrConstraints);
        if (Array.isArray(paths)) {
            return paths.map((path) => this.getRef(path, paramsOrConstraints, params));
        }
        if (hasContraints) {
            const path = pathWithParams(paths, params);
            assertPath(path);
            const ref$1 = ref(this.db, path);
            return query$1(ref$1, ...paramsOrConstraints);
        }
        else {
            const path = pathWithParams(paths, paramsOrConstraints);
            assertPath(path);
            return ref(this.db, path);
        }
    }
    fromQuery(query) {
        let existing = null;
        for (const [key, value] of this.memory.entries()) {
            if (query.isEqual(key)) {
                existing = value;
                break;
            }
        }
        if (existing)
            return existing;
        this.memory.set(query, fromQuery(query));
        return this.memory.get(query);
    }
    create(path, content) {
        return set(this.getRef(path), content);
    }
    update(path, value) {
        const ref = this.getRef(path);
        return update(ref, value);
    }
    remove(path) {
        const ref = this.getRef(path);
        return remove(ref);
    }
}
FireDatabase.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireDatabase, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FireDatabase.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireDatabase, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireDatabase, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/** Recursively all Date into Timestamp */
function fromDate(target) {
    if (typeof target !== 'object')
        return target;
    for (const key in target) {
        const value = target[key];
        if (!value || typeof value !== 'object')
            continue;
        if (value instanceof Date) {
            target[key] = value.getTime();
            continue;
        }
        fromDate(value);
    }
    return target;
}
/** Recursively all Date into Timestamp */
function toDate(target, dateKeys, path = '') {
    if (typeof target !== 'object')
        return target;
    for (const key in target) {
        const value = target[key];
        const deepKey = `${path}.${key}`;
        if (dateKeys.includes(deepKey)) {
            if (typeof value !== 'number')
                throw new Error(`Date key "${deepKey}" is not a number. Got ${value}`);
            target[key] = new Date(value);
            continue;
        }
        if (!value || typeof value !== 'object')
            continue;
        toDate(value, dateKeys, deepKey);
    }
    return target;
}

function isListQuery(query) {
    if (typeof query === 'string')
        return false;
    if (Array.isArray(query) && isIdList(query))
        return false;
    return true;
}
function toKey(value) {
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number')
        return value.toString();
    throw new Error('Key of list should either be a string or a number');
}
function addMeta(doc, actionType) {
    const _meta = doc['_meta'] ?? {};
    if (actionType === 'add')
        _meta.createdAt = serverTimestamp();
    if (actionType === 'update')
        _meta.modifiedAt = serverTimestamp();
    doc._meta = _meta;
}
class FireList {
    constructor() {
        this.fireDB = inject(FireDatabase);
    }
    fromDatabase(snap) {
        if (!snap.exists())
            return null;
        const value = snap.val();
        const dateKeys = [...this.dateKeys, '_meta.createdAt', '_meta.modifiedAt'];
        if (!value || typeof value !== 'object')
            return toDate(value, dateKeys);
        if (this.idKey)
            value[this.idKey] = snap.key;
        if (this.pathKey)
            value[this.pathKey] = snap.ref.toString();
        return toDate(value, dateKeys);
    }
    toDatabase(doc, actionType) {
        return fromDate(doc);
    }
    toData(snaps, options) {
        if (!snaps)
            return null;
        if (Array.isArray(snaps))
            return snaps.map(snap => this.toData(snap, { isList: false })).filter(exist$1);
        if (!options.isList)
            return this.fromDatabase(snaps);
        const docs = [];
        // forEach cancels when return value is "true". So I return "false"
        snaps.forEach(snap => !docs.push(this.fromDatabase(snap)));
        return docs.filter(exist$1);
    }
    getPath(key, params) {
        if (typeof key === 'string')
            return pathWithParams(`${this.path}/${key}`, params);
        return pathWithParams(this.path, key);
    }
    getRef(query, params) {
        // String or Params (getPath return base path is query is Params)
        if (!Array.isArray(query))
            return this.fireDB.getRef(this.getPath(query), params);
        return isIdList(query)
            // key list
            ? this.fireDB.getRef(query.map(key => this.getPath(key)), params)
            // query constraints
            : this.fireDB.getRef(this.getPath(), query, params);
    }
    fromQuery(query, params) {
        const refs = this.getRef(query, params);
        if (!Array.isArray(refs))
            return this.fireDB.fromQuery(refs);
        const obs = refs.map(ref => this.fireDB.fromQuery(ref));
        return combineLatest(obs);
    }
    getQuery(query, params) {
        const refs = this.getRef(query, params);
        if (!Array.isArray(refs))
            return get(refs);
        const promises = refs.map(ref => get(ref));
        return Promise.all(promises);
    }
    valueChanges(query, params) {
        if (arguments.length && !query)
            return of(null);
        return this.fromQuery(query, params).pipe(map(snap => this.toData(snap, { isList: isListQuery(query) })));
    }
    async getValue(query, params) {
        if (arguments.length && !query)
            return Promise.resolve(null);
        const snap = await this.getQuery(query, params);
        return this.toData(snap, { isList: isListQuery(query) });
    }
    add(value, params) {
        const doc = this.toDatabase(value, 'add');
        if (this.idKey && doc[this.idKey]) {
            const key = toKey(doc[this.idKey]);
            const ref = this.getRef(key, params);
            return set(ref, doc);
        }
        const listRef = params ? this.getRef(params) : this.getRef();
        return push(listRef, doc);
    }
    update(key, value, params) {
        const doc = this.toDatabase(value, 'update');
        const path = this.getRef(key, params);
        return update(path, doc);
    }
    remove(key, params) {
        const ref = this.getRef(key, params);
        return remove(ref);
    }
    /** We use a separated method to avoid mistakes */
    removeAll(params) {
        const ref = params ? this.getRef(params) : this.getRef();
        return remove(ref);
    }
}

function fromTask(task) {
    return new Observable((subscriber) => {
        const progress = (snap) => subscriber.next(snap);
        const error = (e) => subscriber.error(e);
        const complete = () => subscriber.complete();
        // emit the current state of the task
        progress(task.snapshot);
        // emit progression of the task
        const unsubscribeFromOnStateChanged = task.on('state_changed', progress);
        // use the promise form of task, to get the last success snapshot
        task.then((snapshot) => {
            progress(snapshot);
            setTimeout(() => complete(), 0);
        }, (e) => {
            progress(task.snapshot);
            setTimeout(() => error(e), 0);
        });
        // the unsubscribe method returns by storage isn't typed in the
        // way rxjs expects, Function vs () => void, so wrap it
        return function unsubscribe() {
            unsubscribeFromOnStateChanged();
        };
    }).pipe(
    // since we're emitting first the current snapshot and then progression
    // it's possible that we could double fire synchronously; namely when in
    // a terminal state (success, error, canceled). Debounce to address.
    debounceTime(0));
}
function percentage(task) {
    return fromTask(task).pipe(map((snapshot) => ({
        progress: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        snapshot,
    })));
}

const GET_FIRE_STORAGE = new InjectionToken('Firebase Storage', {
    providedIn: 'root',
    factory: () => {
        const storages = {};
        const config = getConfig();
        const app = inject(FIREBASE_APP);
        return (bucket) => {
            const name = bucket ?? 'default';
            if (!storages[name]) {
                storages[name] = getStorage(app, bucket);
                if (config.storage)
                    config.storage(storages[name]);
            }
            return storages[name];
        };
    },
});

class FireStorage {
    constructor() {
        this.getStorage = inject(GET_FIRE_STORAGE);
    }
    get storage() {
        return this.getStorage(this.bucket);
    }
    ref(url) {
        return ref$1(this.storage, url);
    }
    upload(url, bytes, metadata) {
        const ref = this.ref(url);
        return uploadBytesResumable(ref, bytes, metadata);
    }
}
FireStorage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireStorage, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FireStorage.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireStorage, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: FireStorage, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ANALYTICS_SETTINGS, BaseFireAuth, CLOUD_FUNCTIONS, CallableFunctions, FIREBASE_APP, FIREBASE_CONFIG, FIRESTORE, FIRESTORE_SETTINGS, FIRE_ANALYTICS, FIRE_AUTH, FireAuth, FireCollection, FireDatabase, FireList, FireStorage, FireSubCollection, FirestoreService, GET_FIRE_STORAGE, REGION_OR_DOMAIN, addMeta, fromRef, fromTask, getConfig, getCustomClaims, getDocPath, getPathParams, isContraintList, isUpdateCallback, joinWith, percentage, shareWithDelay, toDate$1 as toDate, user };
